/**
 * Review deck logic — the parts that decide what the coach sees and what the product may do.
 *
 * Kept free of React so the same rules drive the deck, the counts and the export gate, and so
 * there is exactly ONE definition of each rule (binding decisions, REVIEW card):
 *   - confidence bands: ≥0.85 green, 0.70–0.84 yellow, <0.70 red   (implementation-plan.md §1.3c)
 *   - nothing exports unconfirmed                                   (plan §D4, THE contract)
 */
import type { ClipStatus, StructuredClipRow, VocabularyTerm } from '../api/client';

export type { StructuredClipRow, VocabularyTerm };
/** Kept as an alias: the capture-card seam and the E2E harness already speak `ClipProposal`. */
export type ClipProposal = StructuredClipRow;

export type ConfidenceBand = 'green' | 'yellow' | 'red';

export const CONFIDENCE_THRESHOLDS = { green: 0.85, yellow: 0.7 } as const;

/** The band the card's badge uses. Red means "needs attention", not "wrong". */
export function confidenceBand(confidence: number): ConfidenceBand {
  if (confidence >= CONFIDENCE_THRESHOLDS.green) return 'green';
  if (confidence >= CONFIDENCE_THRESHOLDS.yellow) return 'yellow';
  return 'red';
}

export function confidenceLabel(confidence: number): string {
  return `${confidence.toFixed(2)} ${confidenceBand(confidence)}`;
}

/** The band's own word for the coach. A red card is not a verdict, it is a request to look. */
export function bandAttentionLabel(band: ConfidenceBand): string | null {
  return band === 'red' ? 'needs attention' : null;
}

/**
 * Model flags, in the coach's language. Unknown flags pass through verbatim rather than being
 * hidden — a flag the UI silently drops is a flag the coach never gets to weigh.
 */
const FLAG_LABELS: Record<string, string> = {
  vague_boundary: 'vague boundary',
  no_vocab_match: 'no vocab match',
  self_contradiction: 'self contradiction',
};

export function flagLabel(flag: string): string {
  return FLAG_LABELS[flag] ?? flag.replace(/_/g, ' ');
}

/** What the coach's card actually shows: their edit when they made one, else the AI's proposal. */
export function effectiveLabel(clip: StructuredClipRow): string {
  return clip.edited_label ?? clip.label;
}

export function effectiveNotes(clip: StructuredClipRow): string {
  return clip.edited_notes ?? clip.notes;
}

export function effectiveWindow(clip: StructuredClipRow): { tIn: number; tOut: number } {
  return {
    tIn: clip.edited_t_in ?? clip.t_in,
    tOut: clip.edited_t_out ?? clip.t_out,
  };
}

export function effectiveVocabRef(clip: StructuredClipRow): string | null {
  return clip.edited_vocab_ref ?? clip.vocab_ref;
}

export function wasEdited(clip: StructuredClipRow): boolean {
  return (
    clip.edited_label !== null ||
    clip.edited_notes !== null ||
    clip.edited_t_in !== null ||
    clip.edited_t_out !== null ||
    clip.edited_vocab_ref !== null
  );
}

/** Resolved clips are the coach's decision — they leave the un-decided queue. */
export function isResolved(clip: StructuredClipRow): boolean {
  return clip.status !== 'proposed';
}

/** Everything still waiting for a decision, in the order the AI proposed them. */
export function pendingQueue(clips: StructuredClipRow[]): StructuredClipRow[] {
  return clips.filter((clip) => !isResolved(clip));
}

export interface ReviewSummary {
  total: number;
  /** still `proposed` — waiting on the coach */
  awaiting: number;
  confirmed: number;
  edited: number;
  rejected: number;
  /** confirmed + edited: clips the coach has said yes to (edited still needs confirm) */
  approved: number;
  /** resolved = every clip has a decision (confirmed / edited / rejected) */
  resolved: number;
  /** confidence < 0.70 — surfaced, never auto-decided */
  needsAttention: number;
}

export function reviewSummary(clips: StructuredClipRow[]): ReviewSummary {
  const count = (status: ClipStatus) => clips.filter((clip) => clip.status === status).length;
  const confirmed = count('confirmed');
  const edited = count('edited');
  return {
    total: clips.length,
    awaiting: count('proposed'),
    confirmed,
    edited,
    rejected: count('rejected'),
    approved: confirmed + edited,
    resolved: clips.filter(isResolved).length,
    needsAttention: clips.filter((clip) => confidenceBand(clip.confidence) === 'red').length,
  };
}

export const EMPTY_SUMMARY: ReviewSummary = {
  total: 0,
  awaiting: 0,
  confirmed: 0,
  edited: 0,
  rejected: 0,
  approved: 0,
  resolved: 0,
  needsAttention: 0,
};

/**
 * THE gate. A single confirmed clip is what unlocks download/export; zero confirmed means the
 * UI must not even render the affordance (plan §D4).
 */
export function canExport(summary: Pick<ReviewSummary, 'confirmed'>): boolean {
  return summary.confirmed > 0;
}

/** Clips the batch button would confirm: everything the coach has not decided yet. */
export function batchConfirmTargets(clips: StructuredClipRow[]): StructuredClipRow[] {
  return clips.filter((clip) => clip.status === 'proposed' || clip.status === 'edited');
}

export function batchConfirmLabel(clips: StructuredClipRow[]): string | null {
  const targets = batchConfirmTargets(clips);
  if (targets.length === 0) return null;
  const total = clips.length;
  // "Confirm all 7 clips" when nothing has been decided yet, otherwise name what is left.
  return targets.length === total
    ? `Confirm all ${total} clip${total === 1 ? '' : 's'}`
    : `Confirm remaining ${targets.length}`;
}

/**
 * The end-of-deck prompt. Only when every clip has a decision AND at least one was approved —
 * "N clips approved. Render now?".
 */
export function completionPrompt(summary: ReviewSummary): string | null {
  if (summary.total === 0) return null;
  if (summary.resolved !== summary.total) return null;
  if (summary.confirmed === 0) {
    return 'No clips approved — nothing will be exported.';
  }
  return `${summary.confirmed} clip${summary.confirmed === 1 ? '' : 's'} approved. Render now?`;
}

/** mm:ss.ss for a clip boundary. Frames matter at this scale, so hundredths are kept. */
export function formatClock(seconds: number): string {
  const safe = Number.isFinite(seconds) && seconds > 0 ? seconds : 0;
  const minutes = Math.floor(safe / 60);
  const rest = safe - minutes * 60;
  return `${String(minutes).padStart(2, '0')}:${rest.toFixed(2).padStart(5, '0')}`;
}

export function formatRange(clip: StructuredClipRow): string {
  const { tIn, tOut } = effectiveWindow(clip);
  return `${formatClock(tIn)} → ${formatClock(tOut)}`;
}

/** Swipe gestures (brand direction §3): left = keep and next, down = discard. */
export const SWIPE_THRESHOLD_PX = 60;

export type SwipeDirection = 'keep' | 'discard' | null;

export function classifySwipe(dx: number, dy: number): SwipeDirection {
  if (Math.abs(dx) < SWIPE_THRESHOLD_PX && Math.abs(dy) < SWIPE_THRESHOLD_PX) return null;
  if (Math.abs(dx) > Math.abs(dy)) return dx < 0 ? 'keep' : null;
  return dy > 0 ? 'discard' : null;
}

/** The clip window must stay sane on the client too — the worker enforces it as the authority. */
export function clampWindow(
  tIn: number,
  tOut: number,
  sourceDuration: number | null,
): { tIn: number; tOut: number } {
  const upper = sourceDuration && sourceDuration > 0 ? sourceDuration : Number.POSITIVE_INFINITY;
  const start = Math.max(0, Math.min(tIn, upper));
  const end = Math.max(start + 0.1, Math.min(tOut, upper));
  return { tIn: Number(start.toFixed(2)), tOut: Number(end.toFixed(2)) };
}

export function vocabLabel(terms: VocabularyTerm[], key: string | null): string | null {
  if (!key) return null;
  return terms.find((term) => term.key === key)?.label ?? key;
}
